<template>
  <div class="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4 sm:px-6 lg:px-8">
    <div class="max-w-4xl mx-auto">
      <div class="text-center mb-8">
        <h1 class="text-4xl font-bold text-gray-900 mb-2">📋 Your Packing Lists</h1>
        <p class="text-lg text-gray-600">Manage and organize all your packing lists</p>
      </div>

      <div class="mb-6 flex flex-col sm:flex-row justify-between items-center gap-4">
        <router-link 
          to="/"
          class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Create New List
        </router-link>

        <div class="text-sm text-gray-500">
          {{ packingLists.length }} list{{ packingLists.length !== 1 ? 's' : '' }} total
        </div>
      </div>

      <div v-if="loading" class="text-center py-8">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <p class="mt-2 text-gray-600">Loading your packing lists...</p>
      </div>

      <div v-else-if="error" class="text-center py-8">
        <div class="text-red-600 mb-4">
          <svg class="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <p class="text-lg font-medium">Error loading packing lists</p>
          <p class="text-sm">{{ error }}</p>
        </div>
        <button
          @click="loadAllLists"
          class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Try Again
        </button>
      </div>

      <div v-else-if="packingLists.length === 0" class="text-center py-12">
        <svg class="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
        <h3 class="text-xl font-medium text-gray-900 mb-2">No packing lists yet</h3>
        <p class="text-gray-500 mb-6">Create your first packing list to get started!</p>
        <router-link
          to="/"
          class="inline-flex items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Create New List
        </router-link>
      </div>

      <div v-else class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <div
          v-for="list in packingLists"
          :key="list.id"
          class="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden"
        >
          <div class="p-6">
            <div class="flex justify-between items-start mb-3">
              <h3 class="text-lg font-semibold text-gray-900 truncate flex-1 mr-2">
                {{ list.title }}
              </h3>
              <button
                @click="confirmDelete(list)"
                class="text-gray-400 hover:text-red-500 transition-colors duration-200 flex-shrink-0"
                title="Delete list"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
            
            <p v-if="list.description" class="text-gray-600 text-sm mb-4 line-clamp-2">
              {{ list.description }}
            </p>
            
            <div class="text-xs text-gray-500 mb-4">
              Created: {{ formatDate(list.created) }}
              <br v-if="list.updated !== list.created">
              <span v-if="list.updated !== list.created">
                Updated: {{ formatDate(list.updated) }}
              </span>
            </div>

            <div class="flex justify-between items-center">
              <router-link
                :to="`/list/${list.id}`"
                class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
              >
                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                View List
              </router-link>
              
              <span class="text-xs text-gray-500">
                ID: {{ list.id.slice(-8) }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Delete Confirmation Modal -->
      <div v-if="listToDelete" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" @click="cancelDelete">
        <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white" @click.stop>
          <div class="mt-3">
            <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg class="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 class="text-lg font-medium text-gray-900 text-center mb-2">Delete Packing List</h3>
            <p class="text-sm text-gray-500 text-center mb-4">
              Are you sure you want to delete "<strong>{{ listToDelete.title }}</strong>"? This action cannot be undone and will also delete all categories and items in this list.
            </p>
            <div class="flex justify-center space-x-4">
              <button
                @click="cancelDelete"
                class="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
              >
                Cancel
              </button>
              <button
                @click="deleteList"
                :disabled="deleting"
                class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
              >
                <span v-if="deleting">Deleting...</span>
                <span v-else>Delete</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import type { PackingList } from '@/types'
import { pocketbaseService } from '@/services/pocketbase'

const packingLists = ref<PackingList[]>([])
const loading = ref(false)
const error = ref<string | null>(null)
const listToDelete = ref<PackingList | null>(null)
const deleting = ref(false)

const formatDate = (dateString: string) => {
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch {
    return dateString
  }
}

const loadAllLists = async () => {
  loading.value = true
  error.value = null
  
  try {
    packingLists.value = await pocketbaseService.getAllPackingLists()
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to load packing lists'
    console.error('Failed to load packing lists:', err)
  } finally {
    loading.value = false
  }
}

const confirmDelete = (list: PackingList) => {
  listToDelete.value = list
}

const cancelDelete = () => {
  listToDelete.value = null
  deleting.value = false
}

const deleteList = async () => {
  if (!listToDelete.value) return
  
  deleting.value = true
  try {
    await pocketbaseService.deletePackingList(listToDelete.value.id)
    packingLists.value = packingLists.value.filter(list => list.id !== listToDelete.value!.id)
    cancelDelete()
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to delete packing list'
    console.error('Failed to delete packing list:', err)
    deleting.value = false
  }
}

onMounted(() => {
  loadAllLists()
})
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>